import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MarketRequest, MarketsService } from '@src/app/shared/backend/clients';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-add-market',
    templateUrl: './add-market.component.html',
    styleUrl: './add-market.component.scss',
    host: {
        class: 'app-add-market'
    },
    standalone: false
})
export class AddMarketComponent extends BaseViewComponent implements OnInit {

  comboData: ComboData = new ComboData(async (filter) => await firstValueFrom(this.marketsService.apiMarketsFilterComboPost(filter)));

  marketsList: any;
  marketsTotalRecords: any;
  marketsRowsPerPageOption: number = 10;

  public marketForm: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(50)]],
    code: ['', [Validators.required, Validators.maxLength(50)]],
    warrantyDurationMonths: ['', [Validators.required]],
    hasParentMarket: ['false'],
    marketId: [[]]
  },
    { validators: this.conditionalValidators() });

  constructor(
    private router: Router,
    private marketsService: MarketsService,
    private fb: FormBuilder
  ) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    await this.initComboData();
  }

  private async initComboData() {
    await this.comboData.getData(null, 1);
  }

  async onLazyLoadComboData(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.comboData.getData(search, activePage);
  }

  getComboTotalPages(): number {
    const totalRecords = this.comboData?.totalRecords ?? 0;
    const pageSize = Number(this.comboData?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  conditionalValidators(): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const hasParentMarket = formGroup.get('hasParentMarket')?.value;
      const market = formGroup.get('marketId')?.value;

      const hasSelectedMarket = Array.isArray(market)
        ? market.length > 0
        : !!market;

      if (hasParentMarket === 'true' && !hasSelectedMarket) {
        return { requiredMarket: true };
      }

      return null;
    };
  }

  isMarketRequired(): boolean {
    return this.marketForm.get('hasParentMarket')?.value === 'true';
  }

  async submitForm() {
    if (this.marketForm.valid) {
      const payload: MarketRequest = {
        name: this.marketForm.controls['name'].value,
        code: this.marketForm.controls['code'].value,
        parentId: this.marketForm.controls['marketId'].value?.[0],
        warrantyDurationMonths: this.marketForm.controls['warrantyDurationMonths'].value
      };

      await firstValueFrom(this.marketsService.apiMarketsCreatePost(payload));

      this.marketForm.controls['name'].setValue(null);
      this.marketForm.controls['code'].setValue(null);
      this.marketForm.controls['marketId'].setValue([]);
      this.marketForm.controls['warrantyDurationMonths'].setValue(null);

      this.router.navigate(['/markets']);
    }
  }
}
